/**
 * Automatically bundles Qalzy App Premium with the AI Scale and keeps them in sync.
 *
 * DISABLED FOR NOW — set SCALE_BUNDLE_ENABLED to true to re-enable auto-adding
 * Premium when the scale is added to cart (and related cart sync/lock behaviour).
 */
const SCALE_BUNDLE_ENABLED = false;

class ScaleBundle {
  constructor(config) {
    this.config = config || {};
    this._enforcing = false;
    this._enforceQueued = false;
  }

  get enabled() {
    return SCALE_BUNDLE_ENABLED;
  }

  get aiScaleVariantIds() {
    return this.config.aiScaleVariantIds || [];
  }

  get premiumVariantId() {
    return this.config.premiumVariantId;
  }

  get premiumSellingPlanId() {
    return this.config.premiumSellingPlanId || null;
  }

  isAiScaleVariant(variantId) {
    const id = parseInt(variantId, 10);
    return this.aiScaleVariantIds.includes(id);
  }

  isPremiumVariant(variantId) {
    return parseInt(variantId, 10) === this.premiumVariantId;
  }

  isAiScaleHandle(handle) {
    return handle === this.config.aiScaleHandle;
  }

  getProductHandleFromElement(element) {
    const productRoot = element?.closest?.('product-info, [data-product-info]');
    const productUrl = productRoot?.dataset?.url;
    if (!productUrl) return null;
    return productUrl.split('/products/')[1]?.split('?')[0]?.split('#')[0] || null;
  }

  shouldBundleWithPremium({ productHandle, variantId }) {
    if (productHandle && this.isAiScaleHandle(productHandle)) return true;
    if (variantId && this.isAiScaleVariant(variantId)) return true;
    return false;
  }

  cartHasAiScale(items = []) {
    return items.some((item) => this.isAiScaleVariant(item.variant_id || item.id));
  }

  getPremiumCartItem(items = []) {
    return items.find((item) => this.isPremiumVariant(item.variant_id || item.id));
  }

  getPremiumCartLines(items = []) {
    return items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => this.isPremiumVariant(item.variant_id || item.id));
  }

  isLockedPremiumLine(item, items = []) {
    if (!item) return false;
    const variantId = item.variant_id || item.id;
    if (!this.isPremiumVariant(variantId)) return false;
    return this.cartHasAiScale(items);
  }

  buildPremiumCartItem(quantity = 1) {
    const item = {
      id: this.premiumVariantId,
      quantity,
      properties: {
        _qalzy_scale_bundle: 'true',
      },
    };

    if (this.premiumSellingPlanId) {
      item.selling_plan = this.premiumSellingPlanId;
    }

    return item;
  }

  isBundledPremiumItem(item) {
    if (!item?.properties) return false;

    const props = item.properties;

    if (Array.isArray(props)) {
      return props.some(
        (property) =>
          (property.key === '_qalzy_scale_bundle' || property.name === '_qalzy_scale_bundle') &&
          String(property.value) === 'true'
      );
    }

    return props._qalzy_scale_bundle === 'true';
  }

  shouldRemovePremiumWhenScaleGone(item) {
    return this.isBundledPremiumItem(item);
  }

  async removeAllBundledPremium(items, cartComponent, cartData) {
    let updatedCartData = cartData;
    let currentItems = updatedCartData?.items || items;

    let bundledPremiumLines = this.getPremiumCartLines(currentItems).filter(({ item }) =>
      this.shouldRemovePremiumWhenScaleGone(item)
    );

    while (bundledPremiumLines.length > 0) {
      const targetLine = bundledPremiumLines[bundledPremiumLines.length - 1].line;
      const removalResult = await this.removePremiumFromCart(currentItems, cartComponent, targetLine);

      if (typeof removalResult === 'string') {
        updatedCartData = JSON.parse(removalResult);
      } else if (removalResult) {
        updatedCartData = removalResult;
      } else {
        break;
      }

      currentItems = updatedCartData?.items || [];
      bundledPremiumLines = this.getPremiumCartLines(currentItems).filter(({ item }) =>
        this.shouldRemovePremiumWhenScaleGone(item)
      );
    }

    return updatedCartData;
  }

  async buildScaleRemovalRequest(line, quantity, variantId, cartComponent) {
    const sections = cartComponent?.getSectionsToRender
      ? cartComponent.getSectionsToRender().map((section) => section.section)
      : [];
    const sectionsUrl = window.location.pathname;

    const changePayload = {
      line,
      quantity,
      sections,
      sections_url: sectionsUrl,
    };

    if (!this.enabled || parseInt(quantity, 10) !== 0 || !this.isAiScaleVariant(variantId)) {
      return {
        url: window.routes.cart_change_url,
        body: changePayload,
      };
    }

    const cart = await this.fetchCart();
    const updates = {};

    cart.items.forEach((item) => {
      const itemVariantId = item.variant_id || item.id;

      if (
        this.isAiScaleVariant(itemVariantId) ||
        (this.isPremiumVariant(itemVariantId) && this.shouldRemovePremiumWhenScaleGone(item))
      ) {
        updates[String(itemVariantId)] = 0;
      }
    });

    if (Object.keys(updates).length > 1) {
      return {
        url: `${window.routes.cart_update_url}.js`,
        body: {
          updates,
          sections,
          sections_url: sectionsUrl,
        },
      };
    }

    return {
      url: window.routes.cart_change_url,
      body: changePayload,
    };
  }

  sortItemsScaleBeforePremium(items = []) {
    const scaleItems = [];
    const premiumItems = [];
    const otherItems = [];

    items.forEach((item) => {
      const variantId = parseInt(item.id, 10);
      if (this.isAiScaleVariant(variantId)) {
        scaleItems.push(item);
      } else if (this.isPremiumVariant(variantId)) {
        premiumItems.push(item);
      } else {
        otherItems.push(item);
      }
    });

    return [...scaleItems, ...premiumItems, ...otherItems];
  }

  appendPremiumIfNeeded(items, context = {}) {
    if (!this.enabled) return items;
    if (!this.premiumVariantId) return items;

    const shouldAdd = this.shouldBundleWithPremium(context);
    if (!shouldAdd) return items;

    const alreadyIncluded = items.some((item) => this.isPremiumVariant(item.id));
    if (alreadyIncluded) return this.sortItemsScaleBeforePremium(items);

    return this.sortItemsScaleBeforePremium([...items, this.buildPremiumCartItem(1)]);
  }

  cartHasPremium(items = []) {
    return Boolean(this.getPremiumCartItem(items));
  }

  async prepareCartAddItems(items, context = {}) {
    if (!this.enabled) return { items, cartItems: [] };

    let prepared = [...items];
    let cartItems = [];

    try {
      cartItems = (await this.fetchCart()).items || [];
    } catch (error) {
      return { items: prepared, cartItems };
    }

    const shouldBundle =
      this.shouldBundleWithPremium(context) || this.cartHasAiScale(cartItems);

    if (!shouldBundle) {
      return { items: prepared, cartItems };
    }

    prepared = prepared.filter((item) => {
      const variantId = parseInt(item.id, 10);
      if (!this.isAiScaleVariant(variantId)) return true;
      return !cartItems.some((cartItem) => (cartItem.variant_id || cartItem.id) === variantId);
    });

    if (!this.cartHasPremium(cartItems)) {
      prepared = this.appendPremiumIfNeeded(prepared, context);
    } else {
      prepared = prepared.filter((item) => !this.isPremiumVariant(item.id));
    }

    prepared = this.sortItemsScaleBeforePremium(prepared);

    return { items: prepared, cartItems };
  }

  isScaleMaxQuantityError(response) {
    const message = `${response?.message || ''} ${response?.description || ''}`.toLowerCase();
    return response?.status === 422 && message.includes('maximum quantity');
  }

  async fetchCart() {
    const response = await fetch(`${window.routes.cart_url}.js`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  /**
   * Returns a cart payload shaped like a cart AJAX response (with a `sections`
   * map keyed by the requested section ids) so a cart component can render and
   * open itself even when no line item was actually added or changed.
   */
  async fetchRenderableCart(sectionIds = []) {
    const ids = sectionIds.filter(Boolean);
    if (!ids.length) return null;

    try {
      const response = await fetch(`${window.routes.cart_update_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({
          sections: ids,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async addPremiumToCart(cartComponent) {
    const payload = {
      items: [this.buildPremiumCartItem(1)],
    };

    if (cartComponent?.getSectionsToRender) {
      payload.sections = cartComponent.getSectionsToRender().map((section) => section.section);
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      ...fetchConfig(),
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to add premium item to cart');
    }

    return response.json();
  }

  getPremiumCartLine(items = []) {
    const index = items.findIndex((item) => this.isPremiumVariant(item.variant_id || item.id));
    return index === -1 ? null : index + 1;
  }

  async removePremiumFromCart(items, cartComponent, line = null) {
    const targetLine = line || this.getPremiumCartLine(items);
    if (!targetLine) return null;

    const body = {
      line: targetLine,
      quantity: 0,
    };

    if (cartComponent?.getSectionsToRender) {
      body.sections = cartComponent.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${window.routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      });

      if (response.headers.get('content-type')?.includes('application/json')) {
        return response.json();
      }

      return response.text();
    } catch (error) {
      console.warn('Scale bundle premium removal blocked:', error);
      return null;
    }
  }

  async setPremiumQuantity(items, quantity, cartComponent) {
    const line = this.getPremiumCartLine(items);
    if (!line) return null;

    const body = {
      line,
      quantity,
    };

    if (cartComponent?.getSectionsToRender) {
      body.sections = cartComponent.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
    }

    const response = await fetch(`${window.routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });

    if (response.headers.get('content-type')?.includes('application/json')) {
      return response.json();
    }

    return response.text();
  }

  getSectionHtml(cartData, section) {
    if (!cartData?.sections || !section) return null;
    return cartData.sections[section.section] || cartData.sections[section.id] || null;
  }

  renderCartSections(cartData, cartComponent) {
    if (!cartData?.sections || !cartComponent?.getSectionsToRender) return;

    cartComponent.getSectionsToRender().forEach((section) => {
      const sectionHtml = this.getSectionHtml(cartData, section);
      if (!sectionHtml) return;

      const target = document.getElementById(section.id);
      if (!target) return;

      const elementToReplace = target.querySelector(section.selector) || target;
      const nextHtml = cartComponent.getSectionInnerHTML(sectionHtml, section.selector);
      if (!nextHtml) return;

      elementToReplace.innerHTML = nextHtml;
    });

    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    const itemCount = cartData.item_count ?? 0;

    cartComponent.classList.toggle('is-empty', itemCount === 0);
    if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', itemCount === 0);
  }

  async enforceCartBundle(cartData, cartComponent) {
    if (!this.enabled) return cartData;
    if (!this.premiumVariantId || this._enforcing) {
      this._enforceQueued = true;
      return cartData;
    }

    this._enforcing = true;

    try {
      const items = (await this.fetchCart()).items || [];
      const hasScale = this.cartHasAiScale(items);
      const premiumItem = this.getPremiumCartItem(items);
      const cartItemsComponent =
        cartComponent || document.querySelector('cart-drawer-items') || document.querySelector('cart-items');

      let updatedCartData = cartData;

      if (hasScale && !premiumItem) {
        updatedCartData = await this.addPremiumToCart(cartItemsComponent);
      } else if (!hasScale) {
        updatedCartData = await this.removeAllBundledPremium(items, cartItemsComponent, updatedCartData);
      } else if (hasScale && premiumItem && premiumItem.quantity !== 1 && this.isBundledPremiumItem(premiumItem)) {
        const changeResult = await this.setPremiumQuantity(items, 1, cartItemsComponent);
        if (typeof changeResult === 'string') {
          updatedCartData = JSON.parse(changeResult);
        } else {
          updatedCartData = changeResult;
        }
      }

      const latestItems = updatedCartData?.items || items;
      if (hasScale) {
        const premiumLines = this.getPremiumCartLines(latestItems);
        if (premiumLines.length > 1) {
          for (let i = premiumLines.length - 1; i >= 1; i -= 1) {
            const removalResult = await this.removePremiumFromCart(
              latestItems,
              cartItemsComponent,
              premiumLines[i].line
            );
            if (typeof removalResult === 'string') {
              updatedCartData = JSON.parse(removalResult);
            } else if (removalResult) {
              updatedCartData = removalResult;
            }
          }
        }
      }

      if (updatedCartData && updatedCartData !== cartData) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'scale-bundle',
          cartData: updatedCartData,
        });
      }

      return updatedCartData;
    } catch (error) {
      console.error('Scale bundle enforcement failed:', error);
      return cartData;
    } finally {
      this._enforcing = false;

      if (this._enforceQueued) {
        this._enforceQueued = false;
        await this.enforceCartBundle(null, cartComponent);
      }
    }
  }

  isLockedCartLineElement(lineElement) {
    return lineElement?.dataset?.locked === 'true';
  }

  getCartLineElement(line) {
    return (
      document.getElementById(`CartDrawer-Item-${line}`) ||
      document.getElementById(`CartItem-${line}`)
    );
  }

  shouldBlockCartChange(line, quantity) {
    if (!this.enabled) return false;

    const lineElement = this.getCartLineElement(line);
    if (!this.isLockedCartLineElement(lineElement)) return false;

    const parsedQuantity = parseInt(quantity, 10);
    return parsedQuantity === 0 || parsedQuantity > 1;
  }
}

window.ScaleBundle = new ScaleBundle(window.scaleBundleConfig || {});

// Disabled for now — see SCALE_BUNDLE_ENABLED at the top of this file.
if (SCALE_BUNDLE_ENABLED && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'scale-bundle') return;
    window.ScaleBundle.enforceCartBundle(event.cartData);
  });
}

if (SCALE_BUNDLE_ENABLED) {
  document.addEventListener('DOMContentLoaded', () => {
    window.ScaleBundle.enforceCartBundle();
  });
}
